/* Copyright (c) 2023 Xperi. All Rights Reserved. */

import { LicensePlate, SamlToken } from 'client-code/device-binding-service';
import { AuthConfiguration, IptvLoginResponse } from 'client-code/iptv-login';
import { DEFAULT_RETRY_OPTIONS, MILLISECONDS_IN_A_SECOND } from '../../config';
import { AuthServices } from '../infrastructure/AuthServices';
import { DeviceBindingService } from '../infrastructure/DeviceBindingService';
import { IptvLoginService } from '../infrastructure/IptvLoginService';
import { IAuthServices } from '../interfaces/IAuthServices';
import { RecoveryMechanism } from '../recoverymechanism/RecoveryMechanism';
import { RetryMechanism } from '../retrymechanism/RetryMechanism';
import { RetryOptions } from '../retrymechanism/RetryOptions';
import { EmptyResponseError, ErrorTypes, LpExpiredError, PartialResponseError } from '../error';
import { Locale } from '../locale/Locale';
import { LocaleKeys } from '../locale/LocaleKeys';
import { DefaultServicesClientImpl } from './DefaultServiceClientImpl';
import { BrandingUiInfoKeys } from '../entities/BrandingUiInfoKeys';

export class AuthServicesClientImpl implements IAuthServices {
    constructor(endpoints: { iptvUrl: string; deviceBinding: string }) {
        if (!endpoints.iptvUrl) throw new Error('IptvServiceUrl not found.');
        IptvLoginService.initialize(endpoints.iptvUrl);
        if (!endpoints.deviceBinding) throw new Error('DeviceBindingServiceUrl not found.');
        DeviceBindingService.initialize(endpoints.deviceBinding);
    }

    private defaultServices = new DefaultServicesClientImpl();

    getAuthConfiguration = (): Promise<AuthConfiguration> => {
        return new Promise((resolve, reject) => {
            const authServices = new AuthServices();

            const getAuthConfiguration = authServices.getAuthConfiguration.bind(authServices);

            RetryMechanism.callServiceWithRetry<AuthConfiguration>(
                getAuthConfiguration,
                [],
                <RetryOptions>DEFAULT_RETRY_OPTIONS,
            )
                .then((authConfig: AuthConfiguration) => {
                    this.validateAuthConfigResponse(authConfig);
                    resolve(authConfig);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    };

    getLicensePlate = (): Promise<LicensePlate> => {
        return new Promise((resolve, reject) => {
            const authServices = new AuthServices();
            const getLicensePlate = authServices.getLicensePlate.bind(authServices);

            RetryMechanism.callServiceWithRetry<LicensePlate>(
                getLicensePlate,
                [],
                <RetryOptions>DEFAULT_RETRY_OPTIONS,
            )
                .then((licensePlate: LicensePlate) => {
                    this.validateLicensePlateResponse(licensePlate);
                    resolve(licensePlate);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    };

    getNewLicensePlate = (): Promise<LicensePlate> => {
        return new Promise((resolve, reject) => {
            const authServices = new AuthServices();
            const getNewLicensePlate = authServices.getNewLicensePlate.bind(authServices);

            RetryMechanism.callServiceWithRetry<LicensePlate>(
                getNewLicensePlate,
                [],
                <RetryOptions>DEFAULT_RETRY_OPTIONS,
            )
                .then((newlicensePlate: LicensePlate) => {
                    this.validateLicensePlateResponse(newlicensePlate);
                    resolve(newlicensePlate);
                })
                .catch((lpError) => {
                    reject(lpError);
                });
        });
    };

    getSamlToken = (): Promise<SamlToken> => {
        return new Promise((resolve, reject) => {
            const authServices = new AuthServices();
            const getSamlToken = authServices.getSamlTokenByLicensePlateCode.bind(authServices);

            RetryMechanism.callServiceWithRetry<SamlToken>(
                getSamlToken,
                [],
                <RetryOptions>DEFAULT_RETRY_OPTIONS,
            )
                .then((samlToken: SamlToken) => {
                    this.validateSamlTokenResponse(samlToken);
                    resolve(samlToken);
                })
                .catch((samlError) => {
                    reject(samlError);
                });
        });
    };

    getSamlTokenByLicensePlateCode = (code: string): Promise<SamlToken> => {
        return new AuthServices().getSamlTokenByLicensePlateCode(code);
    };

    pollSamlToken = (licensePlate: LicensePlate): Promise<SamlToken> => {
        const lpExpiration = new Date(licensePlate.expirationTime);
        return new Promise((resolve, reject) => {
            setTimeout(async () => {
                try {
                    if (Date.now() >= lpExpiration.getTime()) {
                        reject(new LpExpiredError());
                        return;
                    }
                    const samlToken = await this.getSamlTokenByLicensePlateCode(
                        licensePlate.licensePlateCode,
                    );
                    this.validateSamlTokenResponse(samlToken);
                    resolve(samlToken);
                } catch (error) {
                    if (
                        error instanceof PartialResponseError ||
                        error instanceof EmptyResponseError
                    ) {
                        reject(error);
                    } else {
                        resolve(this.pollSamlToken(licensePlate));
                    }
                }
            }, licensePlate.pollingIntervalSeconds * MILLISECONDS_IN_A_SECOND);
        });
    };

    // TODO:Move to common place
    private formatErrorDescription = (description: string) => {
        let formattedDescription = description;
        formattedDescription = formattedDescription.replace(
            'PHONE_CUSTOMER_SUPPORT_NUMBER',
            this.defaultServices.getBrandingUiInfoForKey(
                BrandingUiInfoKeys.PHONE_CUSTOMER_SUPPORT_NUMBER,
            ),
        );
        formattedDescription = formattedDescription.replace(
            'CUSTOMER_SUPPORT_NAME',
            this.defaultServices.getBrandingUiInfoForKey(BrandingUiInfoKeys.CUSTOMER_SUPPORT_NAME),
        );
        return formattedDescription;
    };

    private isResponseIncomplete = (keys: string[], response) => {
        const responseKeys = Object.keys(response);
        for (let i = 0; i < keys.length; i += 1) {
            if (!responseKeys.includes(keys[i])) {
                return true;
            }
        }
        return false;
    };

    private validateAuthConfigResponse = (authConfig) => {
        if (!Object.keys(authConfig).length) {
            throw new EmptyResponseError(
                ErrorTypes.SIGN_IN_AUTH_SERVICE_EMPTY_RESPONSE_C305,
                'Received empty response for auth configuration',
                this.formatErrorDescription(
                    Locale.getString(LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION),
                ),
            );
        }
        const authKeys = ['authenticationType', 'domain', 'licensePlateUrl'];
        if (this.isResponseIncomplete(authKeys, authConfig)) {
            throw new PartialResponseError(
                ErrorTypes.SIGN_IN_AUTH_SERVICE_PARTIAL_RESPONSE_C304,
                'Received partial response for auth service',
                this.formatErrorDescription(
                    Locale.getString(LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION),
                ),
            );
        }
    };

    private validateLicensePlateResponse = (licensePlate) => {
        if (!Object.keys(licensePlate).length) {
            throw new EmptyResponseError(
                ErrorTypes.SIGN_IN_LICENSE_PLATE_EMPTY_RESPONSE_C506,
                'Received empty response for license plate',
                this.formatErrorDescription(
                    Locale.getString(LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION),
                ),
            );
        }
        const lpKeys = ['expirationTime', 'licensePlateCode', 'pollingIntervalSeconds'];
        if (this.isResponseIncomplete(lpKeys, licensePlate)) {
            throw new PartialResponseError(
                ErrorTypes.SIGN_IN_LICENSE_PLATE_PARTIAL_RESPONSE_C505,
                'Received partial response for license plate',
                this.formatErrorDescription(
                    Locale.getString(LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION),
                ),
            );
        }
    };

    private validateSamlTokenResponse = (samlToken) => {
        if (!Object.keys(samlToken).length) {
            throw new EmptyResponseError(
                ErrorTypes.SIGN_IN_SAML_TOKEN_EMPTY_RESPONSE_C407,
                'Received empty response for saml token',
                this.formatErrorDescription(
                    Locale.getString(LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION),
                ),
            );
        }
        const samlKeys = ['samlToken', 'samlTokenExpirationTime'];
        if (this.isResponseIncomplete(samlKeys, samlToken)) {
            throw new PartialResponseError(
                ErrorTypes.SIGN_IN_SAML_TOKEN_PARTIAL_RESPONSE_C408,
                'Received partial response for saml token',
                this.formatErrorDescription(
                    Locale.getString(LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION),
                ),
            );
        }
    };

    private validateIptvLoginResponse = (iptvLoginResponse) => {
        if (!Object.keys(iptvLoginResponse).length) {
            throw new EmptyResponseError(
                ErrorTypes.SIGN_IN_IPTV_LOGIN_EMPTY_RESPONSE_C610,
                'Received empty response for Iptv login',
                this.formatErrorDescription(
                    Locale.getString(LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION),
                ),
            );
        }
        const iptvKeys = ['domainToken', 'accessToken', 'bodyId', 'tokenExpirationTime'];

        if (this.isResponseIncomplete(iptvKeys, iptvLoginResponse)) {
            throw new PartialResponseError(
                ErrorTypes.SIGN_IN_IPTV_LOGIN_PARTIAL_RESPONSE_C609,
                'Received partial response for iptv login',
                this.formatErrorDescription(
                    Locale.getString(LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION),
                ),
            );
        }
    };

    waitForAuthConfiguration = (): Promise<AuthConfiguration> => {
        return new Promise((resolve, reject) => {
            const authServices = new AuthServices();

            const getAuthConfiguration = authServices.getAuthConfiguration.bind(authServices);

            RecoveryMechanism.callServiceWithRecovery<AuthConfiguration>(getAuthConfiguration, [])
                .then((authConfig: AuthConfiguration) => {
                    this.validateAuthConfigResponse(authConfig);
                    resolve(authConfig);
                })
                .catch((authError) => {
                    if (
                        authError instanceof PartialResponseError ||
                        authError instanceof EmptyResponseError
                    ) {
                        resolve(this.waitForAuthConfiguration());
                    } else reject(authError);
                });
        });
    };

    waitForLicensePlate = (): Promise<LicensePlate> => {
        return new Promise((resolve, reject) => {
            const authServices = new AuthServices();
            const getLicensePlate = authServices.getLicensePlate.bind(authServices);

            RecoveryMechanism.callServiceWithRecovery<LicensePlate>(getLicensePlate, [])
                .then((licensePlate: LicensePlate) => {
                    this.validateLicensePlateResponse(licensePlate);
                    resolve(licensePlate);
                })
                .catch((error) => {
                    if (
                        error instanceof PartialResponseError ||
                        error instanceof EmptyResponseError
                    ) {
                        resolve(this.waitForLicensePlate());
                    } else reject(error);
                });
        });
    };

    waitForNewLicensePlate = (): Promise<LicensePlate> => {
        return new Promise((resolve, reject) => {
            const authServices = new AuthServices();
            const getNewLicensePlate = authServices.getNewLicensePlate.bind(authServices);

            RecoveryMechanism.callServiceWithRecovery<LicensePlate>(getNewLicensePlate, [])
                .then((newLicensePlate: LicensePlate) => {
                    this.validateLicensePlateResponse(newLicensePlate);
                    resolve(newLicensePlate);
                })
                .catch((lpError) => {
                    if (
                        lpError instanceof PartialResponseError ||
                        lpError instanceof EmptyResponseError
                    ) {
                        resolve(this.waitForNewLicensePlate());
                    } else reject(lpError);
                });
        });
    };

    waitForIptvLogin = (samlToken): Promise<IptvLoginResponse> => {
        return new Promise((resolve, reject) => {
            const authServices = new AuthServices();
            const getIptvLogin = authServices.getIptvLoginBySamlToken.bind(authServices);

            RecoveryMechanism.callServiceWithRecovery<IptvLoginResponse>(getIptvLogin, [samlToken])
                .then((iptvLoginRes: IptvLoginResponse) => {
                    this.validateIptvLoginResponse(iptvLoginRes);
                    resolve(iptvLoginRes);
                })
                .catch((iptvError) => {
                    if (
                        iptvError instanceof PartialResponseError ||
                        iptvError instanceof EmptyResponseError
                    ) {
                        resolve(this.waitForIptvLogin(samlToken));
                    } else reject(iptvError);
                });
        });
    };

    getIptvLoginBySamlToken = (samlToken: string): Promise<IptvLoginResponse> => {
        return new Promise((resolve, reject) => {
            const authServices = new AuthServices();
            const getIptvLoginBySamlToken = authServices.getIptvLoginBySamlToken.bind(authServices);

            RetryMechanism.callServiceWithRetry<IptvLoginResponse>(
                getIptvLoginBySamlToken,
                [samlToken],
                <RetryOptions>DEFAULT_RETRY_OPTIONS,
            )
                .then((iptvLoginResponse: IptvLoginResponse) => {
                    this.validateIptvLoginResponse(iptvLoginResponse);
                    resolve(iptvLoginResponse);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    };

    waitForSamlToken = (): Promise<SamlToken> => {
        return new Promise((resolve, reject) => {
            const authServices = new AuthServices();
            const getSamlToken = authServices.getSamlTokenByLicensePlateCode.bind(authServices);
            RecoveryMechanism.callServiceWithRecovery<SamlToken>(getSamlToken, [])
                .then((samlToken) => {
                    this.validateSamlTokenResponse(samlToken);
                    resolve(samlToken);
                })
                .catch((samlError) => {
                    if (
                        samlError instanceof PartialResponseError ||
                        samlError instanceof EmptyResponseError
                    ) {
                        resolve(this.waitForSamlToken());
                    } else reject(samlError);
                });
        });
    };

    removeDeviceBinding = (): Promise<void> => {
        return new Promise((resolve, reject) => {
            const authServices = new AuthServices();
            const removeDeviceBinding = authServices.removeDeviceBinding.bind(authServices);
            RetryMechanism.callServiceWithRetry(
                removeDeviceBinding,
                [],
                <RetryOptions>DEFAULT_RETRY_OPTIONS,
            )
                .then(() => resolve())
                .catch((error) => reject(error));
        });
    };
}
